import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import Button from '@/components/base/Button';
import StatCard from '@/components/base/StatCard';
import Modal from '@/components/base/Modal';
import Badge from '@/components/base/Badge';
import ExportMenu from '@/components/feature/ExportMenu';
import { buildCsv } from '@/utils/export';
import { useRole } from '@/context/RoleContext';
import { formatMoney } from '@/utils/format';
import { api, apiList } from '@/utils/api';
import { isApprovable, summarizeChecks, toKesEquivalent } from '@/utils/invoice';
import type { Invoice } from '@/types/portal';
import InvoiceFilterBar from './components/InvoiceFilterBar';
import InvoiceTable from './components/InvoiceTable';
import InvoiceBulkBar from './components/InvoiceBulkBar';

type DecisionMode = 'approve' | 'reject';

export default function Invoices() {
  const { can } = useRole();
  const canApprove = can('invoices.approve');

  const [status, setStatus] = useState('all');
  // The "invoices awaiting approval" alert opens ?status=Pending review (also when already here).
  const statusParam = useSearchParams()[0].get('status');
  useEffect(() => {
    if (statusParam) setStatus(statusParam);
  }, [statusParam]);
  const [markets, setMarkets] = useState<string[]>([]);
  const [query, setQuery] = useState('');
  const [advanceOnly, setAdvanceOnly] = useState(false);
  const [blockingOnly, setBlockingOnly] = useState(false);
  const [selected, setSelected] = useState<string[]>([]);
  const [allInvoices, setAllInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [actionError, setActionError] = useState('');
  const [saving, setSaving] = useState(false);

  const loadInvoices = () =>
    apiList<Invoice>('/invoices')
      .then(({ data }) => {
        setAllInvoices(data);
        setLoadError('');
      })
      .catch((error: Error) => setLoadError(error.message))
      .finally(() => setLoading(false));

  useEffect(() => {
    void loadInvoices();
  }, []);
  const [notice, setNotice] = useState('');
  const [decision, setDecision] = useState<{ mode: DecisionMode; ids: string[] } | null>(null);
  const [note, setNote] = useState('');

  const statusOf = (invoice: Invoice) => invoice.status;

  const counts = useMemo(() => {
    const result: Record<string, number> = { all: allInvoices.length };
    allInvoices.forEach((invoice) => {
      result[invoice.status] = (result[invoice.status] ?? 0) + 1;
    });
    return result;
  }, [allInvoices]);

  const baseFiltered = useMemo(() => {
    return allInvoices.filter((invoice) => {
      if (status !== 'all' && invoice.status !== status) return false;
      if (blockingOnly && isApprovable(invoice)) return false;
      if (markets.length > 0 && !markets.includes(invoice.market)) return false;
      if (query.trim()) {
        const text = query.toLowerCase();
        if (
          !invoice.number.toLowerCase().includes(text) &&
          !invoice.creatorName.toLowerCase().includes(text) &&
          !invoice.campaign.toLowerCase().includes(text)
        ) {
          return false;
        }
      }
      return true;
    });
  }, [allInvoices, status, markets, query, blockingOnly]);

  const advanceCount = useMemo(
    () => baseFiltered.filter((invoice) => invoice.advance).length,
    [baseFiltered],
  );

  const filtered = useMemo(
    () => (advanceOnly ? baseFiltered.filter((invoice) => invoice.advance) : baseFiltered),
    [baseFiltered, advanceOnly],
  );

  const awaiting = allInvoices.filter((invoice) => statusOf(invoice) === 'Pending review');
  const onHold = allInvoices.filter((invoice) => statusOf(invoice) === 'On hold');
  const approved = allInvoices.filter((invoice) => statusOf(invoice) === 'Approved');
  const blocking = allInvoices.filter((invoice) => !isApprovable(invoice));

  const applyStatusCard = (next: string) => {
    setStatus((prev) => (prev === next && !blockingOnly ? 'all' : next));
    setBlockingOnly(false);
    setAdvanceOnly(false);
  };

  const applyBlockingCard = () => {
    setBlockingOnly((prev) => !prev);
    setStatus('all');
    setAdvanceOnly(false);
  };

  const handleStatus = (value: string) => {
    setStatus(value);
    setBlockingOnly(false);
  };

  const outstandingKes = awaiting.reduce(
    (sum, invoice) => sum + toKesEquivalent(invoice.amount, invoice.currency),
    0,
  );

  const selectedInvoices = allInvoices.filter((invoice) => selected.includes(invoice.id));
  const selectedCurrencies = Array.from(new Set(selectedInvoices.map((i) => i.currency)));
  const selectedTotalLabel =
    selectedCurrencies.length === 1
      ? formatMoney(
          selectedInvoices.reduce((sum, invoice) => sum + invoice.amount, 0),
          selectedCurrencies[0],
        )
      : null;
  const approvableCount = selectedInvoices.filter(isApprovable).length;

  const toggleOne = (id: string) =>
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const toggleAll = () =>
    setSelected((prev) => {
      const ids = filtered.map((invoice) => invoice.id);
      const everySelected = ids.every((id) => prev.includes(id));
      return everySelected ? prev.filter((id) => !ids.includes(id)) : Array.from(new Set([...prev, ...ids]));
    });

  const openDecision = (mode: DecisionMode, ids: string[]) => {
    setNote('');
    setDecision({ mode, ids });
  };

  // InvoiceController::bulkAction: approve skips paid invoices; reject records the note as the reason.
  const applyDecision = async () => {
    if (!decision) return;
    setSaving(true);
    setActionError('');
    try {
      const result = await api<{ changed: number; skipped: number }>('/invoices/bulk-action', {
        method: 'POST',
        body: JSON.stringify({ ids: decision.ids, action: decision.mode, note }),
      });
      setSelected((prev) => prev.filter((id) => !decision.ids.includes(id)));
      const verb = decision.mode === 'approve' ? 'approved' : 'rejected';
      setNotice(
        `${result.changed} invoice${result.changed === 1 ? '' : 's'} ${verb}.` +
          (result.skipped > 0 ? ` ${result.skipped} skipped (already paid).` : ''),
      );
      setDecision(null);
      setNote('');
      await loadInvoices();
    } catch (error) {
      setActionError(error instanceof Error ? error.message : 'Could not save the decision.');
    } finally {
      setSaving(false);
    }
  };

  const decisionInvoices = decision
    ? allInvoices.filter((invoice) => decision.ids.includes(invoice.id))
    : [];
  const blockedInSelection = decisionInvoices.filter((invoice) => !isApprovable(invoice));

  const buildInvoiceCsv = () =>
    buildCsv(
      [
        'Invoice',
        'Creator',
        'Campaign',
        'Market',
        'Currency',
        'Amount',
        'Status',
        'Rail',
        'Issued',
        'Due',
        'Flags',
      ],
      filtered.map((invoice) => [
        invoice.number,
        invoice.creatorName,
        invoice.campaign,
        invoice.market,
        invoice.currency,
        invoice.amount,
        statusOf(invoice),
        invoice.rail,
        invoice.issuedAt,
        invoice.dueAt,
        invoice.flags.join(' / '),
      ]),
    );

  return (
    <div className="mx-auto w-full max-w-[1400px] px-4 py-6 pb-28 md:px-6 md:py-8">
      <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
        <div>
          <span className="page-rule" aria-hidden="true" />
          <h1 className="mt-1 font-heading text-2xl font-semibold text-foreground-950 md:text-3xl">
            Invoices
          </h1>
          <p className="mt-1 max-w-2xl text-sm text-foreground-600">
            Review deliverables, rates and compliance, then approve individually or in bulk.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <ExportMenu
            filename="twiva-invoices"
            pdfTitle="Invoices"
            buildCsv={buildInvoiceCsv}
            onExported={(format) =>
              setNotice(
                format === 'csv'
                  ? `CSV exported — ${filtered.length} invoice${filtered.length === 1 ? '' : 's'}.`
                  : 'PDF ready — choose "Save as PDF" in the print dialog.',
              )
            }
          />
          <Button
            icon="ri-check-double-line"
            className="join-shine"
            onClick={() => openDecision('approve', filtered.filter(isApprovable).map((i) => i.id))}
            disabled={!canApprove}
          >
            Approve all ready
          </Button>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <StatCard
          label="Awaiting approval"
          value={String(awaiting.length)}
          icon="ri-hourglass-line"
          tone="secondary"
          hint={`KES equiv ${formatMoney(outstandingKes, 'KES')}`}
          onClick={() => applyStatusCard('Pending review')}
          active={status === 'Pending review' && !blockingOnly}
        />
        <StatCard
          label="On hold"
          value={String(onHold.length)}
          icon="ri-pause-circle-line"
          tone="primary"
          hint="Blocked by KYC or wallet issues"
          onClick={() => applyStatusCard('On hold')}
          active={status === 'On hold' && !blockingOnly}
        />
        <StatCard
          label="Approved"
          value={String(approved.length)}
          icon="ri-checkbox-circle-line"
          tone="accent"
          hint="Queued for the next payout batch"
          onClick={() => applyStatusCard('Approved')}
          active={status === 'Approved' && !blockingOnly}
        />
        <StatCard
          label="Blocking checks"
          value={String(blocking.length)}
          icon="ri-error-warning-line"
          tone="neutral"
          hint="Invoices that cannot be approved yet"
          onClick={applyBlockingCard}
          active={blockingOnly}
        />
      </div>

      {notice ? (
        <div className="mt-5 flex items-center justify-between gap-3 rounded-md border border-accent-200 bg-accent-50 px-4 py-2.5">
          <span className="inline-flex items-center gap-2 text-sm text-accent-800">
            <i className="ri-checkbox-circle-line" />
            {notice}
          </span>
          <button
            type="button"
            onClick={() => setNotice('')}
            className="cursor-pointer text-accent-700 hover:text-accent-900"
            aria-label="Dismiss"
          >
            <i className="ri-close-line" />
          </button>
        </div>
      ) : null}

      {!canApprove ? (
        <div className="mt-5 flex items-center gap-2 rounded-md border border-background-200 bg-background-100 px-4 py-2.5 text-sm text-foreground-600">
          <i className="ri-lock-line" />
          Your current role can view invoices but not approve them. Switch to Finance or Administrator
          to take action.
        </div>
      ) : null}

      <div className="mt-6">
        <InvoiceFilterBar
          status={status}
          markets={markets}
          query={query}
          counts={counts}
          advanceOnly={advanceOnly}
          advanceCount={advanceCount}
          blockingOnly={blockingOnly}
          blockingCount={blocking.length}
          onStatus={handleStatus}
          onSetMarkets={setMarkets}
          onQuery={setQuery}
          onToggleAdvance={() => setAdvanceOnly((prev) => !prev)}
          onToggleBlocking={applyBlockingCard}
        />
      </div>

      <div className="mt-4">
        {loading ? (
          <div className="flex items-center justify-center rounded-lg border border-background-200 bg-background-50 py-16 text-sm text-foreground-500">
            <i className="ri-loader-4-line mr-2 animate-spin" />
            Loading invoices…
          </div>
        ) : loadError ? (
          <div className="rounded-lg border border-primary-200 bg-primary-50 py-12 text-center text-sm text-primary-800">
            <i className="ri-error-warning-line mr-1" />
            {loadError}
          </div>
        ) : (
          <InvoiceTable
            rows={filtered}
            selected={selected}
            onToggle={toggleOne}
            onToggleAll={toggleAll}
            statusOf={statusOf}
          />
        )}
      </div>

      <InvoiceBulkBar
        count={selected.length}
        totalLabel={selectedTotalLabel}
        approvableCount={approvableCount}
        canApprove={canApprove}
        onApprove={() => openDecision('approve', selected)}
        onReject={() => openDecision('reject', selected)}
        onClear={() => setSelected([])}
      />

      <Modal
        open={decision !== null}
        onClose={() => {
          setDecision(null);
          setActionError('');
        }}
        icon={decision?.mode === 'approve' ? 'ri-check-double-line' : 'ri-close-circle-line'}
        title={
          decision?.mode === 'approve'
            ? `Approve ${decisionInvoices.length} invoice${decisionInvoices.length === 1 ? '' : 's'}`
            : `Reject ${decisionInvoices.length} invoice${decisionInvoices.length === 1 ? '' : 's'}`
        }
        subtitle={
          decision?.mode === 'approve'
            ? 'Approved invoices are queued for the next payout batch.'
            : 'Rejected invoices are returned to the creator with your note.'
        }
        footer={
          <>
            <Button
              variant="ghost"
              onClick={() => {
                setDecision(null);
                setActionError('');
              }}
            >
              Cancel
            </Button>
            <Button
              variant={decision?.mode === 'approve' ? 'primary' : 'accent'}
              icon={decision?.mode === 'approve' ? 'ri-check-line' : 'ri-close-line'}
              onClick={() => void applyDecision()}
              disabled={saving || (decision?.mode === 'reject' && !note.trim())}
            >
              Confirm
            </Button>
          </>
        }
      >
        <ul className="divide-y divide-background-100 rounded-md border border-background-200">
          {decisionInvoices.map((invoice) => {
            const summary = summarizeChecks(invoice);
            return (
              <li key={invoice.id} className="flex items-center justify-between gap-3 px-3 py-2.5">
                <div className="min-w-0">
                  <p className="text-sm font-medium text-foreground-900">
                    {invoice.number} · {invoice.creatorName}
                  </p>
                  <p className="truncate text-xs text-foreground-500">{invoice.campaign}</p>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <span className="text-sm text-foreground-800">
                    {formatMoney(invoice.amount, invoice.currency)}
                  </span>
                  <Badge
                    tone={
                      summary.tone === 'primary'
                        ? 'primary'
                        : summary.tone === 'secondary'
                          ? 'secondary'
                          : 'accent'
                    }
                  >
                    {summary.label}
                  </Badge>
                </div>
              </li>
            );
          })}
        </ul>

        {actionError ? (
          <div
            role="alert"
            className="mb-3 flex items-start gap-2 rounded-md border border-primary-200 bg-primary-50 px-3 py-2.5 text-sm text-primary-800"
          >
            <i className="ri-error-warning-line mt-0.5" />
            <span>{actionError}</span>
          </div>
        ) : null}

        {decision?.mode === 'approve' && blockedInSelection.length > 0 ? (
          <div className="mt-3 flex items-start gap-2 rounded-md border border-primary-200 bg-primary-50 px-3 py-2.5 text-sm text-primary-800">
            <i className="ri-error-warning-line mt-0.5" />
            <span>
              {blockedInSelection.length} selected invoice
              {blockedInSelection.length === 1 ? ' has' : 's have'} a blocking check and should be
              reviewed in detail first. They will still be marked approved if you continue.
            </span>
          </div>
        ) : null}

        <div className="mt-4">
          <label
            htmlFor="invoice-decision-note"
            className="text-xs font-label font-medium text-foreground-600"
          >
            {decision?.mode === 'approve' ? 'Approval note (optional)' : 'Reason for rejection (required)'}
          </label>
          <textarea
            id="invoice-decision-note"
            value={note}
            maxLength={500}
            onChange={(e) => setNote(e.target.value)}
            rows={3}
            placeholder={
              decision?.mode === 'approve'
                ? 'Add context for the audit trail...'
                : 'Explain what needs to change...'
            }
            className="mt-1.5 w-full resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-800 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
          />
          <div className="mt-1 flex items-center justify-between text-[11px]">
            <span className="text-primary-700">
              {decision?.mode === 'reject' && !note.trim() ? 'A reason is required to reject invoices.' : ''}
            </span>
            <span className="text-foreground-400">{note.length}/500</span>
          </div>
        </div>
      </Modal>
    </div>
  );
}